/**
 * 企业应用视图 — 100%复刻原型 enterprise-brain-apps-clean.html
 * 使用原型中的CSS类名：store-card, view-tabs, db-search, store-toolbar, app-icon, btn-get
 */
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useToast } from '@/components/Toast/useToast';
import { useAuthStore } from '@/stores/authStore';
import { sanitizeSVG } from '@/utils/sanitize';
import { resolveAppIcon } from '@/utils/appIcons';
import { AppModal } from '@/components/AppModal';
import { AppPublishModal } from '@/components/AppPublishModal';
import { AppShareModal } from '@/components/AppShareModal';
import * as appsApi from '@/services/apps';
import { isSourcePackage, isAIProject, getAppDownloadUrl } from '@/services/apps';
import type { AppItem, AppCategory } from '@/services/apps';
import './AppsView.css';

export type CatItem = AppCategory;

/* ===== SVG 图标路径（UI 通用图标，非应用图标）===== */
const iconPaths: Record<string, string> = {
  search: 'M9 15A6 6 0 1 0 9 3a6 6 0 0 0 0 12Zm4.5-1.5L18 18',
  plus: 'M10 3v14M3 10h14',
  download: 'M10 2v12M5 9l5 5 5-5M2 13v5h16v-5',
  info: 'M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM10 6v5m0 3h.01',
  share: 'M18 5a3 3 0 1 0-6 0 3 3 0 0 0 6 0ZM6 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm12 7a3 3 0 1 0-6 0 3 3 0 0 0 6 0ZM8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98',
  upload: 'M10 14V2M5 7l5-5 5 5M2 13v5h16v-5',
  pencil: 'M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z',
  trash: 'M3 5h14M7 5V2h6v3M5 5l1 13h8l1-13M8 8v6m4-6v6',
  more: 'M4 10h.01M10 10h.01M16 10h.01',
  file: 'M12 2H5v16h10V5l-3-3Zm0 0v4h3M8 10h4m-4 3h4',
};

function Icon({ name, size = 18, className = '' }: { name: string; size?: number; className?: string }) {
  const path = iconPaths[name] || iconPaths.file;
  return (
    <svg className={`icon ${className}`} width={size} height={size} viewBox="0 0 20 20"
      fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
}

interface AppIconProps {
  app: AppItem;
  size?: number;
}

/* ===== AppIcon 组件 ===== */
function AppIcon({ app, size = 48 }: AppIconProps) {
  const { gradient, color, svgPath } = resolveAppIcon(app.cat, app.icon_choice);

  // icon_choice 优先：用户主动选择了图标，显示图标
  // 只有没有 icon_choice 时才显示缩略图
  const hasIconChoice = app.icon_choice && app.icon_choice !== '';
  const thumbUrl = !hasIconChoice && app.thumbnail
    ? `/api/apps/thumbs/${app.thumbnail.replace(/^apps\/thumbs\//, '')}`
    : null;

  const iconSvg = `<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.45" stroke-linecap="round" stroke-linejoin="round"><path d="${svgPath}"/></svg>`;

  if (thumbUrl) {
    return (
      <span className="app-icon app-icon-thumb" style={{ '--app-icon-size': `${size}px` } as React.CSSProperties}>
        <img src={thumbUrl} alt={app.name}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
            const parent = (e.target as HTMLImageElement).parentElement;
            if (parent) {
              parent.classList.remove('app-icon-thumb');
              parent.style.background = gradient;
              parent.style.color = color;
              parent.innerHTML = sanitizeSVG(iconSvg);
            }
          }}
        />
      </span>
    );
  }

  return (
    <span className="app-icon"
      style={{ '--app-icon-size': `${size}px`, background: gradient, color } as React.CSSProperties}
      dangerouslySetInnerHTML={{ __html: sanitizeSVG(iconSvg) }}
    />
  );
}

/* ===== 卡片操作菜单组件 ===== */
interface CardMenuProps {
  app: AppItem;
  isGuest: boolean;
  onShare: (e: React.MouseEvent, app: AppItem) => void;
  onUpdate: (e: React.MouseEvent, app: AppItem) => void;
  onEdit: (e: React.MouseEvent, app: AppItem) => void;
  onDelete: (e: React.MouseEvent, app: AppItem) => void;
}

function CardMenu({ app, isGuest, onShare, onUpdate, onEdit, onDelete }: CardMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [open]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  return (
    <div className="card-menu-wrap" ref={menuRef}>
      <button className="btn btn-icon" onClick={handleToggle} title="更多操作">
        <Icon name="more" size={16} />
      </button>
      {open && (
        <div className="popup-menu">
          <button className="menu-item" onClick={e => { setOpen(false); onShare(e, app); }}>
            <Icon name="share" size={15} />
            <span>分享</span>
          </button>
          {!isGuest && (
            <>
              <button className="menu-item" onClick={e => { setOpen(false); onUpdate(e, app); }}>
                <Icon name="upload" size={15} />
                <span>更新版本</span>
              </button>
              <button className="menu-item" onClick={e => { setOpen(false); onEdit(e, app); }}>
                <Icon name="pencil" size={15} />
                <span>编辑信息</span>
              </button>
              <div className="menu-divider" />
              <button className="menu-item danger" onClick={e => { setOpen(false); onDelete(e, app); }}>
                <Icon name="trash" size={15} />
                <span>删除</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ===== 主组件 ===== */
export function AppsView() {
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const isGuest = user?.role === 'guest';
  const [activeCat, setActiveCat] = useState('all');
  const [search, setSearch] = useState('');
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);

  const [apps, setApps] = useState<AppItem[]>([]);
  const [cats, setCats] = useState<CatItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [deleteConfirm, setDeleteConfirm] = useState<AppItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [detailApp, setDetailApp] = useState<{ app: AppItem; catName: string } | null>(null);
  const [publishModal, setPublishModal] = useState<{ mode: 'create' | 'edit' | 'update'; app?: AppItem } | null>(null);
  const [shareApp, setShareApp] = useState<AppItem | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [apiApps, apiCats] = await Promise.all([
        appsApi.listApps(),
        appsApi.listCategories(),
      ]);
      setApps(apiApps || []);
      setCats(apiCats || []);
    } catch (err) {
      console.error('Failed to load apps:', err);
      setError('加载应用数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const appMatch = useCallback((a: AppItem) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (a.name + a.pub + a.dept + a.desc + a.source).toLowerCase().includes(q);
  }, [search]);

  // 过滤分类选项（排除 all 和 acquired，这两个单独处理）
  const categoryOptions = useMemo(() => {
    return cats.filter(c => c.id !== 'all' && c.id !== 'acquired');
  }, [cats]);

  // 固定的分类顺序和简短名称：全部应用、已获取、销售、运营...
  // 差异02: 使用简短的部门分类名称
  const SHORT_CAT_NAMES: Record<string, string> = {
    sales: '销售', ops: '运营', service: '客服',
    marketing: '市场', ads: '市场',
    design: '设计',
    inventory: '仓库', stock: '仓库',
    finance: '财务', profit: '财务',
  };
  
  const fixedCategories = useMemo(() => {
    const result: { id: string; name: string }[] = [
      { id: 'acquired', name: '已获取' }
    ];
    // 按照原型顺序添加：销售、运营、客服、市场、设计、仓库、财务
    const order = ['sales', 'ops', 'service', 'marketing', 'ads', 'design', 'inventory', 'stock', 'finance', 'profit'];
    for (const id of order) {
      const cat = cats.find(c => c.id === id);
      if (cat) {
        // 使用简短名称
        result.push({ id: cat.id, name: SHORT_CAT_NAMES[cat.id] || cat.name });
      }
    }
    // 添加其他不在顺序中的分类
    for (const cat of categoryOptions) {
      if (!order.includes(cat.id)) {
        result.push(cat);
      }
    }
    return result;
  }, [cats, categoryOptions]);

  // 根据当前分类筛选应用
  const filteredApps = useMemo(() => {
    return apps.filter(a => {
      // 全部应用
      if (activeCat === 'all') return appMatch(a);
      // 已获取（目前显示全部，后续可根据用户获取记录筛选）
      if (activeCat === 'acquired') return appMatch(a);
      // 按分类筛选
      if (a.cat !== activeCat) return false;
      return appMatch(a);
    });
  }, [activeCat, apps, appMatch]);

  const handleCardClick = useCallback((app: AppItem, catName: string) => {
    setDetailApp({ app, catName });
  }, []);

  const handlePublish = useCallback(() => {
    setPublishModal({ mode: 'create' });
  }, []);

  const handleUpdateVersion = useCallback((e: React.MouseEvent, app: AppItem) => {
    e.stopPropagation();
    setPublishModal({ mode: 'update', app });
  }, []);

  const handleShare = useCallback((e: React.MouseEvent, app: AppItem) => {
    e.stopPropagation();
    setShareApp(app);
  }, []);

  const handleEdit = useCallback((e: React.MouseEvent, app: AppItem) => {
    e.stopPropagation();
    setPublishModal({ mode: 'edit', app });
  }, []);

  const handleDelete = useCallback((e: React.MouseEvent, app: AppItem) => {
    e.stopPropagation();
    setDeleteConfirm(app);
  }, []);

  const handleOpenApp = useCallback(async (app: AppItem) => {
    try {
      const token = localStorage.getItem('eb-token');
      const url = appsApi.getAppViewUrl(String(app.id));
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error('无法加载应用');
      }
      const html = await response.text();
      const blob = new Blob([html], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '无法打开应用');
    }
  }, [toast]);

  const handleDownloadApp = useCallback(async (app: AppItem) => {
    try {
      const url = getAppDownloadUrl(String(app.id));
      const token = localStorage.getItem('eb-token');
      const resp = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!resp.ok) throw new Error('下载失败');
      
      const contentDisposition = resp.headers.get('Content-Disposition');
      let filename = app.name;
      if (contentDisposition) {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
        if (utf8Match) {
          filename = decodeURIComponent(utf8Match[1]);
        } else {
          const basicMatch = contentDisposition.match(/filename=["']?([^"';\n]+)/i);
          if (basicMatch) {
            filename = basicMatch[1];
          }
        }
      }
      if (!filename.includes('.')) {
        filename += app.file_type === 'zip' ? '.zip' : '.html';
      }
      
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('已开始下载，请在浏览器中确认');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('下载失败，请重试');
    }
  }, [toast]);

  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await appsApi.deleteApp(String(deleteConfirm.id));
      toast.success('应用已删除');
      setDeleteConfirm(null);
      loadData();
      window.dispatchEvent(new CustomEvent('apps:change'));
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('删除失败，请重试');
    } finally {
      setDeleting(false);
    }
  }, [deleteConfirm, toast, loadData]);

  const handlePublishSubmit = useCallback(async (data: { draft: any; file?: File; fileInfo?: any; version: string; screenshot1?: string; screenshot2?: string }) => {
    try {
      if (publishModal?.mode === 'create') {
        if (!data.file) throw new Error('请选择应用文件');
        
        // 第1步：上传文件
        const uploadResult = await appsApi.uploadAppFile(data.file);
        
        // 第2步：发布应用（JSON 接口）
        await appsApi.publishApp({
          name: data.draft.name,
          desc: data.draft.desc,
          cat: data.draft.cat,
          feats: data.draft.features ? data.draft.features.split('、').filter(Boolean) : [],
          note: data.draft.note || '',
          visibility: (data.draft.visibility || 'public') as 'public' | 'dept',
          file_key: uploadResult.file_key,
          file_type: uploadResult.file_type,
          file_size: uploadResult.file_size,
          entry_file: uploadResult.entry_file,
          source_filename: data.file.name,
          source_sha256: uploadResult.source_sha256,
          app_kind: uploadResult.app_kind,
          instruction_entry: uploadResult.instruction_entry,
          icon_choice: data.draft.iconChoice ? JSON.stringify(data.draft.iconChoice) : undefined,
        });
        toast.success('应用已发布');
      } else if (publishModal?.mode === 'edit' && publishModal.app) {
        await appsApi.updateApp(String(publishModal.app.id), {
          name: data.draft.name,
          desc: data.draft.desc,
          cat: data.draft.cat,
          features: data.draft.features,
          note: data.draft.note,
          visibility: data.draft.visibility,
          api_url: data.draft.apiUrl,
          refresh: data.draft.refresh,
          icon_choice: data.draft.iconChoice ? JSON.stringify(data.draft.iconChoice) : undefined,
          // 截图（base64 data URL，后端会上传到MinIO）
          ...(data.screenshot1 ? { screenshot1: data.screenshot1 } : {}),
          ...(data.screenshot2 ? { screenshot2: data.screenshot2 } : {}),
        } as any);
        toast.success('应用信息已更新');
      } else if (publishModal?.mode === 'update' && publishModal.app) {
        // 更新版本：先上传文件，然后调用 publishApp（带 id）
        if (!data.file) throw new Error('请选择应用文件');
        
        // 第1步：上传文件
        const uploadResult = await appsApi.uploadAppFile(data.file);
        
        // 第2步：发布新版本（带 id 表示更新）
        await appsApi.publishApp({
          id: String(publishModal.app.id),
          name: data.draft.name,
          desc: data.draft.desc,
          cat: publishModal.app.cat,
          feats: data.draft.features ? data.draft.features.split('、').filter(Boolean) : [],
          note: data.draft.note || '版本更新',
          visibility: (data.draft.visibility || publishModal.app.visibility || 'public') as 'public' | 'dept',
          file_key: uploadResult.file_key,
          file_type: uploadResult.file_type,
          file_size: uploadResult.file_size,
          entry_file: uploadResult.entry_file,
          source_filename: data.file.name,
          source_sha256: uploadResult.source_sha256,
          app_kind: uploadResult.app_kind,
          instruction_entry: uploadResult.instruction_entry,
        });
        toast.success(`新版本已发布`);
      }
      setPublishModal(null);
      // 先保存编辑的应用ID（setPublishModal后publishModal变null但闭包里还有值）
      const editedAppId = publishModal?.app ? String(publishModal.app.id) : null;
      // 重新加载列表
      const [updatedApps, updatedCats] = await Promise.all([
        appsApi.listApps(),
        appsApi.listCategories(),
      ]);
      setApps(updatedApps || []);
      setCats(updatedCats || []);
      // 如果详情弹窗正在展示被编辑的应用，同步更新
      if (editedAppId) {
        const freshApp = (updatedApps || []).find(a => String(a.id) === editedAppId);
        if (freshApp) {
          const catName = (updatedCats || []).find(c => c.id === freshApp.cat)?.name || '未分类';
          // 不管详情弹窗有没有打开，都同步更新（如果打开了就能看到新图标）
          setDetailApp(prev => prev && String(prev.app.id) === editedAppId
            ? { app: freshApp, catName }
            : prev
          );
        }
      }
      window.dispatchEvent(new CustomEvent('apps:change'));
    } catch (err) {
      console.error('Publish failed:', err);
      throw new Error('发布失败，请重试');
    }
  }, [publishModal, toast, loadData]);

  // 获取分类名称
  const getCatName = useCallback((catId: string) => {
    const shortName = SHORT_CAT_NAMES[catId];
    if (shortName) return shortName;
    const cat = cats.find(c => c.id === catId);
    return cat?.name || '未分类';
  }, [cats]);

  // 获取分类下应用数量
  const getAppCount = useCallback((catId: string) => {
    if (catId === 'all') return apps.length;
    return apps.filter(a => a.cat === catId).length;
  }, [apps]);

  // 显示的分类选项（收起时显示前3个，展开时显示全部）
  const visibleCategoryOptions = useMemo(() => {
    if (categoriesExpanded) return fixedCategories;
    return fixedCategories.slice(0, 3); // 已获取、销售、运营（全部应用单独渲染）
  }, [fixedCategories, categoriesExpanded]);

  return (
    <div className="doc-page wide without-heading">
      {/* 工具栏：分类标签 + 搜索 + 发布按钮 */}
      <div className="store-toolbar">
        <div className="store-tabs-row">
          <div className="app-category-filter">
            <div className="view-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeCat === 'all'}
                className={`view-tab ${activeCat === 'all' ? 'active' : ''}`}
                onClick={() => setActiveCat('all')}
              >
                全部应用
              </button>
              {visibleCategoryOptions.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  role="tab"
                  aria-selected={activeCat === cat.id}
                  className={`view-tab ${activeCat === cat.id ? 'active' : ''}`}
                  onClick={() => setActiveCat(cat.id)}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            {fixedCategories.length > 3 && (
              <button
                className={`app-category-toggle${categoriesExpanded ? ' expanded' : ''}`}
                type="button"
                aria-label={categoriesExpanded ? '收起分类' : '展开分类'}
                aria-expanded={categoriesExpanded}
                onClick={() => setCategoriesExpanded(!categoriesExpanded)}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m8 5 5 5-5 5" />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div className="db-actions">
          <label className="db-search">
            <Icon name="search" size={16} />
            <input
              type="search"
              placeholder="搜索应用…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="搜索应用"
            />
          </label>
          {!isGuest && (
            <button className="btn btn-primary" onClick={handlePublish}>
              <Icon name="plus" size={14} />
              发布应用
            </button>
          )}
        </div>
      </div>

      {/* Loading 状态 */}
      {loading && (
        <div className="empty-state">
          <div className="loading-spinner" />
          <span>加载应用数据…</span>
        </div>
      )}

      {/* Error 状态 */}
      {error && !loading && (
        <div className="empty-state">
          <Icon name="info" size={40} />
          <strong>{error}</strong>
          <button className="btn btn-primary" onClick={loadData}>重试</button>
        </div>
      )}

      {/* 数据加载成功 */}
      {!loading && !error && (
        <>
          {/* 应用网格 - 使用 store-grid store-grid-2 */}
          {filteredApps.length > 0 ? (
            <div className="store-grid">
              {filteredApps.map(app => {
                const catName = getCatName(app.cat);
                const isSource = isSourcePackage(app);
                const isAI = isAIProject(app);
                const needsDownload = isSource || isAI;

                return (
                  <article key={app.id} className="store-card">
                    <button
                      type="button"
                      className="store-card-main"
                      onClick={() => handleCardClick(app, catName)}
                      aria-label={`查看 ${app.name}`}
                    >
                      <AppIcon app={app} size={48} />
                      <span className="store-card-copy">
                        <strong>{app.name}</strong>
                        <span className="store-card-description">{app.desc}</span>
                        <span className="store-card-meta">
                          {app.dept ? `${app.dept} · ${app.pub}` : app.pub}
                        </span>
                      </span>
                    </button>
                    <div className="store-card-action">
                      <button
                        className="btn btn-get"
                        onClick={e => {
                          e.stopPropagation();
                          handleCardClick(app, catName);
                        }}
                      >
                        查看
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <Icon name="search" size={40} />
              <strong>
                {apps.length === 0 ? '暂无企业应用' : `没有匹配「${search}」的应用`}
              </strong>
              <p>
                {apps.length === 0
                  ? '点击「发布应用」创建第一个应用'
                  : '换个关键词，或点上方分类浏览'}
              </p>
              {apps.length === 0 && (
                <button className="btn btn-primary" onClick={handlePublish}>
                  <Icon name="plus" size={14} />
                  发布应用
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* 删除确认弹窗 */}
      {deleteConfirm && (
        <div className="modal-mask" onClick={() => setDeleteConfirm(null)}>
          <section className="dialog" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <header className="dialog-head">
              <h2>删除应用</h2>
              <button className="btn btn-icon" onClick={() => setDeleteConfirm(null)} aria-label="关闭">
                <Icon name="plus" size={18} className="rotate-45" />
              </button>
            </header>
            <div className="dialog-body">
              <p>确定要删除「{deleteConfirm.name}」吗？此操作不可撤销。</p>
            </div>
            <footer className="dialog-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                取消
              </button>
              <button className="btn btn-primary danger" onClick={confirmDelete} disabled={deleting}>
                {deleting ? '删除中...' : '确认删除'}
              </button>
            </footer>
          </section>
        </div>
      )}

      {/* 应用详情弹窗 */}
      {detailApp && (
        <AppModal
          app={detailApp.app}
          catName={detailApp.catName}
          onClose={() => setDetailApp(null)}
          onOpen={() => handleOpenApp(detailApp.app)}
          onEdit={(app) => {
            setPublishModal({ mode: 'edit', app });
          }}
          onUpdate={(app) => {
            setPublishModal({ mode: 'update', app });
          }}
          onDelete={(app) => {
            setDetailApp(null); // 先关闭详情弹窗
            setDeleteConfirm(app); // 再打开删除确认
          }}
        />
      )}

      {/* 发布/编辑弹窗 */}
      {publishModal && (
        <AppPublishModal
          mode={publishModal.mode}
          existingApp={publishModal.app ? {
            id: publishModal.app.id,
            name: publishModal.app.name,
            desc: publishModal.app.desc,
            cat: String(publishModal.app.cat || publishModal.app.category_id || ''),
            features: publishModal.app.feats?.join('、') || publishModal.app.features || '',
            note: publishModal.app.note,
            visibility: publishModal.app.visibility as 'public' | 'dept',
            apiUrl: publishModal.app.api_url,
            refresh: publishModal.app.refresh,
            version: publishModal.app.version || publishModal.app.ver,
            iconChoice: publishModal.app.icon_choice,
          } : undefined}
          categories={cats.filter(c => c.id !== 'all' && c.id !== 'acquired').map(c => ({ id: String(c.id), name: c.name }))}
          onClose={() => setPublishModal(null)}
          onSubmit={handlePublishSubmit}
        />
      )}

      {/* 分享弹窗 */}
      {shareApp && (
        <AppShareModal
          app={{
            id: shareApp.id,
            name: shareApp.name,
            desc: shareApp.desc,
            visibility: shareApp.visibility as 'public' | 'dept',
          }}
          onClose={() => setShareApp(null)}
        />
      )}
    </div>
  );
}
